import { useTranslation } from 'react-i18next'
import { useState } from 'react'

import { useStatusFilter } from '../hooks/useStatusFilter'
import { useVisibilityFilter } from '../hooks/useVisibilityFilter'
import { useFundraisersActionModal } from '../hooks/useFundraiserActionModal'
import { useReopenFundraiserActionModal } from '../hooks/useReopenFundraiserActionModal'
import { CloseFundriaserModal } from '../FundraisersModal/CloseFundraiserModal/CloseFundraiserModal'
import { ReopenFundraiserModal } from '../FundraisersModal/ReopenFundraiserModal/ReopenFundraiserModal'
import { FundraisersMenu } from '../FundraisersMenu/FundraisersMenu'
import { useFeatureFundraiserActionModal } from '../hooks/useFeatureFundraiserActionModal'
import { FeatureFundraiserModal } from '../FundraisersModal/FeatureFundraiserModal/FeatureFundraiserModal'
import { RemoveFeaturedFundraiserModal } from '../FundraisersModal/FeatureFundraiserModal/RemoveFeaturedFundraiserModal'

import { env } from '@percent/partner-dashboard/env'
import { getFormattedDate, getFormattedDateOnly } from '@percent/partner-dashboard/common/library/utility/date'
import { formatMoney } from '@percent/utility'
import { usePermissionCheck } from '@percent/partner-dashboard/common/hooks/useAllow/usePermissionCheck'
import styles from './FundraisersTable.module.scss'
import { FundraisersTableProps } from './FundraisersTable.types'
import { ErrorView } from '@percent/partner-dashboard/common/components'
import {
  Badge,
  Checkbox,
  Copy,
  FilterList,
  SearchInput,
  Table,
  TableBody,
  TableCell,
  TableColumnSortDirection,
  TablePagination,
  TableProvider,
  TableRow,
  Tooltip
} from '@percent/lemonade'
import {
  FundraiserStatus,
  FundraiserVisibility
} from '@percent/partner-dashboard/services/fundraisers/fundraisersService.types'
import { FundraiserProgress } from '@percent/domain/fundraiser'
import { useSearchFilter } from '@percent/partner-dashboard/common/hooks/useSearchFilter/useSearchFilter'
import { FeaturedStarTooltip } from '@percent/partner-dashboard/common/components/FeaturedStarTooltip/FeaturedStarTooltip'

const fundraiserVisibilityBadgeVariant: Record<FundraiserVisibility, 'informative' | 'default'> = {
  [FundraiserVisibility.PUBLIC]: 'informative',
  [FundraiserVisibility.LISTED]: 'default',
  [FundraiserVisibility.UNLISTED]: 'default'
}

export function FundraisersTable({
  data,
  isLoading,
  totalResults,
  errorMessage,
  nextPage,
  previousPage,
  queryParams,
  setQueryParams,
  refresh
}: Readonly<FundraisersTableProps>) {
  const {
    t,
    i18n: { language: locale }
  } = useTranslation()
  const { selectedFundraiser, isFundraiserModalOpen, openFundraiserModal, closeFundraiserModal } =
    useFundraisersActionModal()
  const {
    selectedReopenFundraiser,
    isReopenFundraiserModalOpen,
    openReopenFundraiserModal,
    closeReopenFundraiserModal
  } = useReopenFundraiserActionModal()
  const {
    selectedFeaturedFundraiser,
    isFeaturedFundraiserModalOpen,
    openFeaturedFundraiserModal,
    closeFeaturedFundraiserModal,
    isRemoveFeaturedFundraiserModalOpen,
    openRemoveFeaturedFundraiserModal,
    closeRemoveFeaturedFundraiserModal
  } = useFeatureFundraiserActionModal()
  const { userCan } = usePermissionCheck()
  const [direction, setDirection] = useState<TableColumnSortDirection>(
    (queryParams.direction as TableColumnSortDirection) || 'DESC'
  )
  const [sortBy, setSortBy] = useState<string>(queryParams.sort || 'featuredAt')

  const handleRequestSort = (columnKey: string) => {
    const newDirection = direction === 'ASC' ? 'DESC' : 'ASC'
    const newSortBy = columnKey

    setDirection(newDirection)
    setSortBy(newSortBy)
    setQueryParams({
      direction: newDirection,
      sort: newSortBy
    })
  }

  const { searchValue, handleChange, handleKeyPress, handleClearValue } = useSearchFilter({
    queryParams,
    setQueryParams
  })

  const handleFeaturedOnlyChange = (checked: boolean) => {
    setQueryParams({
      ...queryParams,
      featuredOnly: checked || undefined
    })
  }

  const { visibilityOptions, selectedVisibility, onVisibilityChange } = useVisibilityFilter({
    queryParams,
    setQueryParams
  })

  const columns = [
    { id: '', isSortable: false, props: { width: '2%' } },
    { id: 'title', content: t('table.header.name'), isSortable: true, props: { width: '20%' } },
    { id: t('table.header.nonprofit'), props: { width: '20%' } },
    { id: t('table.header.organizer'), props: { width: '15%' } },
    { id: 'amount', content: t('table.header.progress'), isSortable: true, props: { width: '10%' } },
    { id: 'createdAt', content: t('table.header.date'), isSortable: true, props: { width: '15%' } },
    { id: t('table.header.visibility'), props: { width: '8%' } },
    { id: t('table.header.status'), props: { width: '6%' } },
    { id: '', isSortable: false, props: { width: '4%' } }
  ]

  const { statusOptions, selectedStatus, onStatusChange } = useStatusFilter({ queryParams, setQueryParams })

  if (errorMessage) {
    return <ErrorView errorMessage={errorMessage} />
  }

  return (
    <>
      <TableProvider
        columns={columns}
        totalResults={totalResults}
        isLoading={isLoading || !data}
        illustration="no-results"
        emptyTableTitle={
          queryParams?.query
            ? t('typography.noSearchTableListTitle', {
                searchTerm: queryParams?.query
              })
            : t('typography.noListTitle', { listType: 'fundraisers' })
        }
        emptyTableDescription={
          queryParams?.query
            ? t('typography.noSearchTableListDescription')
            : t('typography.noListDescription', { listType: 'fundraisers' })
        }
        sortDirection={direction}
        sortByColumn={sortBy}
        handleRequestSort={handleRequestSort}
        disableHorizontalScroll
      >
        <div className={styles.filterContainer}>
          <SearchInput
            name="search"
            value={searchValue}
            onKeyDown={handleKeyPress}
            onChange={handleChange}
            handleClearValue={handleClearValue}
            placeholder={t('table.fundraisersListSearchPlaceholder')}
            status={searchValue.length >= 255 ? 'danger' : 'default'}
            statusMessage={t('errorMessage.shouldNotExceed255')}
          />
          <FilterList
            label={t('table.status')}
            options={statusOptions}
            selectedValues={selectedStatus}
            onChange={onStatusChange}
            testId="fundraiser-status"
          />
          <Checkbox
            name="featured"
            label={t('typography.featuredOnly')}
            variant="default"
            active={queryParams?.featuredOnly || false}
            onChange={e => handleFeaturedOnlyChange(e.currentTarget.checked)}
            data-testid="featured-only-checkbox"
            labelWrap="nowrap"
          />
          <FilterList
            label={t('table.visibility')}
            options={visibilityOptions}
            selectedValues={selectedVisibility}
            onChange={onVisibilityChange}
            testId="fundraiser-visibility"
          />
        </div>
        <div className={styles.tableWrapper}>
          <Table>
            <TableBody>
              {data?.map(fundraiser => {
                const fundraiserUrl = `${env.NX_PUBLIC_WPG_ADDRESS}/fundraisers/${fundraiser.id}`

                return (
                  <TableRow key={fundraiser.id}>
                    {fundraiser.featured && fundraiser.status === FundraiserStatus.Published ? (
                      <TableCell>
                        <FeaturedStarTooltip featured={fundraiser.featured} />
                      </TableCell>
                    ) : (
                      <TableCell>
                        <div />
                      </TableCell>
                    )}
                    <TableCell className={styles.fundraiserNameTableCell}>
                      <Tooltip content={fundraiser.page.title}>
                        <div className={styles.fundraiserNameCell}>
                          <a
                            href={`${env.NX_PUBLIC_WPG_ADDRESS}/fundraisers/${fundraiser.id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={styles.fundraisersNameCell}
                          >
                            <span className={styles.titleWrapper}>
                              <span className={styles.titleText}>{fundraiser.page.title}</span>
                            </span>
                          </a>
                        </div>
                      </Tooltip>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <Copy text={fundraiser.organisation.name} className={styles.copyButton}>
                        <Tooltip content={fundraiser.organisation.name}>
                          <span className={styles.copyText}>{fundraiser.organisation.name}</span>
                        </Tooltip>
                      </Copy>
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      <div className={styles.donorWrapper}>
                        {fundraiser.organiser?.name && (
                          <Copy text={fundraiser.organiser.name} className={styles.copyButton}>
                            <Tooltip content={fundraiser.organiser.name}>
                              <span className={styles.copyText}>{fundraiser.organiser.name}</span>
                            </Tooltip>
                          </Copy>
                        )}
                        {fundraiser.organiser?.email && (
                          <Copy text={fundraiser.organiser.email} className={styles.copyButton}>
                            <span className={styles.email}>{fundraiser.organiser.email}</span>
                          </Copy>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className={styles.fundraiserGoalCell}>
                      {fundraiser.campaign.goal ? (
                        <FundraiserProgress
                          donated={fundraiser.donationTotal}
                          goal={fundraiser.campaign.goal}
                          locale={locale}
                        />
                      ) : (
                        <span data-testid="fundraiser-raised">
                          {t('table.amount_raised', { amount: formatMoney(fundraiser.donationTotal, { locale }) })}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className={styles.tableCell}>
                      {getFormattedDate(fundraiser.createdAt)}
                      {fundraiser.endAt ? (
                        <span className={styles.endDate}>
                          {t('table.header.endsOn', { date: getFormattedDateOnly(fundraiser.endAt) })}
                        </span>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {fundraiser.visibility ? (
                        <Badge
                          variant={fundraiserVisibilityBadgeVariant[fundraiser.visibility]}
                          testId="fundraiser-visibility-badge"
                        >
                          {t(`status.visibility.${fundraiser.visibility}`)}
                        </Badge>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {fundraiser.status === 'published' ? (
                        <Badge variant="positive">{t('status.open')}</Badge>
                      ) : (
                        <Badge variant="default">{t(`status.${fundraiser.status}`)}</Badge>
                      )}
                    </TableCell>
                    <TableCell dataTestId="fundraiser-actions-menu">
                      <FundraisersMenu
                        fundraiser={fundraiser}
                        fundraiserUrl={fundraiserUrl}
                        openFundraiserModal={openFundraiserModal}
                        openReopenFundraiserModal={openReopenFundraiserModal}
                        openFeaturedFundraiserModal={openFeaturedFundraiserModal}
                        openRemoveFeaturedFundraiserModal={openRemoveFeaturedFundraiserModal}
                        userCan={userCan}
                      />
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
        <TablePagination
          totalResultsText={t('table.results', { count: totalResults || undefined })}
          previousPage={previousPage}
          nextPage={nextPage}
        />
      </TableProvider>
      {isFundraiserModalOpen && selectedFundraiser && (
        <CloseFundriaserModal
          isOpen={isFundraiserModalOpen}
          onClose={closeFundraiserModal}
          fundraiser={selectedFundraiser}
          refresh={refresh}
        />
      )}

      {isReopenFundraiserModalOpen && selectedReopenFundraiser && (
        <ReopenFundraiserModal
          isOpen={isReopenFundraiserModalOpen}
          onClose={closeReopenFundraiserModal}
          fundraiser={selectedReopenFundraiser}
          refresh={refresh}
        />
      )}

      {isFeaturedFundraiserModalOpen && selectedFeaturedFundraiser && (
        <FeatureFundraiserModal
          isOpen={isFeaturedFundraiserModalOpen}
          onClose={closeFeaturedFundraiserModal}
          fundraiser={selectedFeaturedFundraiser}
          refresh={refresh}
        />
      )}

      {isRemoveFeaturedFundraiserModalOpen && selectedFeaturedFundraiser && (
        <RemoveFeaturedFundraiserModal
          isOpen={isRemoveFeaturedFundraiserModalOpen}
          onClose={closeRemoveFeaturedFundraiserModal}
          fundraiser={selectedFeaturedFundraiser}
          refresh={refresh}
        />
      )}
    </>
  )
}
